import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { colorCodesForAvatar } from "../../../../constants/index";
import { chracterPosition } from "../../../../utils/index";
import AgentRolePill from "./AgentRolePill";
import { AgentSidebarSkeleton } from "./AgentLoadingSkeletons";
import { AGENT_STATUS_OPTIONS } from "../../../../constants/peopleData";
import { peopleEllipsisSx } from "../peopleFormValidation";

const sidebarCloseButtonSx = {
  p: 0,
  flexShrink: 0,
  overflow: "visible",
  // 24px canvas vs 16px chevrons — nudge so right edges line up without clipping
  mr: "-4px",
  "& svg": {
    width: 24,
    height: 24,
    display: "block",
    overflow: "visible",
  },
};

function DetailChip({ label }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        minWidth: 0,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        color: "#405261",
        fontFamily: "Inter",
        fontSize: 12,
        height: "32px",
      }}
    >
      <Typography component="span" sx={{ ...peopleEllipsisSx, fontSize: "inherit" }} title={label}>
        {label}
      </Typography>
    </Box>
  );
}

function SidebarField({ label, value, title }) {
  return (
    <Box sx={{ mb: 1.5, minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "#405261", mb: 0.25 }}>{label}</Typography>
      <Typography
        sx={{ fontSize: 14, color: "#061522", ...peopleEllipsisSx }}
        title={title ?? value ?? undefined}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ borderBottom: "1px solid #EEF2F6", py: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
      >
        <Typography sx={{ color: "#061522", fontFamily: "Inter", fontSize: 14, fontWeight: 500 }}>
          {title}
        </Typography>
        <Dropdown style={{ width: 16, height: 16, transform: open ? "rotate(180deg)" : "none" }} />
      </Box>
      {open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
}

function PresenceBadge({ presence }) {
  const opt = AGENT_STATUS_OPTIONS.find((o) => o.value === presence);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt?.dot || "#93A1B0" }} />
      <Typography sx={{ fontSize: 14, color: "#061522", fontFamily: "Inter" }}>
        {opt?.label || presence}
      </Typography>
    </Box>
  );
}

export default function AgentDetailSidebar({ agent, loading, onClose, onViewDetail, onEdit, canEdit = true }) {
  if (!agent && !loading) return null;

  const letter = (agent?.name || "").substring(0, 1).toUpperCase();

  return (

    loading ? (
      <>
      <Box sx={{ position: "fixed", inset: 0, bgcolor: "rgba(6, 21, 34, 0.2)", zIndex: 1200 }} />
      <Box sx={{ position: "fixed", top: 0, right: 0, width: 280, height: "100vh", bgcolor: "#FFF", boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)", zIndex: 1201, display: "flex", flexDirection: "column" }}>
        <AgentSidebarSkeleton />
      </Box>
      </>
    ) : (
    <>
      <Box onClick={onClose} sx={{ position: "fixed", inset: 0, bgcolor: "rgba(6, 21, 34, 0.2)", zIndex: 1200 }} />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 280,
          height: "100vh",
          bgcolor: "#FFF",
          boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        { !agent ? (
          <Box sx={{ flex: 1, px: 2, py: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#405261" }}>
              Unable to load agent details.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
                {agent.avatar ? (
                  <Avatar src={agent.avatar} alt={agent.name} sx={{ width: 40, height: 40, flexShrink: 0 }} />
                ) : (
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      bgcolor: colorCodesForAvatar[chracterPosition(letter)] || "#405261",
                    }}
                  >
                    {letter}
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      color: "#061522",
                      fontSize: 14,
                      fontWeight: 500,
                      ...peopleEllipsisSx,
                    }}
                    title={agent.name}
                  >
                    {agent.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#405261",
                      fontSize: 14,
                      ...peopleEllipsisSx,
                    }}
                    title={agent.jobTitle || agent.role || undefined}
                  >
                    {agent.jobTitle || agent.role || "—"}
                  </Typography>
                </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  borderBottom: "1px solid #E5E9EC",
                  mx: -2,
                  flexShrink: 0,
                }}
              />

              <CollapsibleSection title="Agent Information">
                <SidebarField label="Email" value={agent.email} title={agent.email} />
                <Typography sx={{ fontSize: 12, color: "#405261", mb: 0.25 }}>Status</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <PresenceBadge presence={agent.presence} />
                </Box>
                <Typography sx={{ fontSize: 12, color: "#405261", mb: 0.25 }}>Role</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <AgentRolePill role={agent.role} />
                </Box>
                <SidebarField label="Chat Limit" value={agent.chatLimit} />
                <SidebarField label="Performance" value={`${agent.chats} / Conversations`} />
                <SidebarField label="Last Activity" value={agent.lastActivityDate || agent.lastActivity || "—"} />
              </CollapsibleSection>

              <CollapsibleSection title="Groups & Teams">
                <Typography sx={{ fontSize: 12, color: "#405261", mb: 0.75 }}>Teams</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                  {agent.teams?.length ? (
                    agent.teams.map((t) => <DetailChip key={t} label={t} />)
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "#405261" }}>—</Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: "#405261", mb: 0.75 }}>Groups</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {agent.groups?.length ? (
                    agent.groups.map((g) => <DetailChip key={g} label={g} />)
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "#405261" }}>—</Typography>
                  )}
                </Box>
              </CollapsibleSection>

              {agent.kbPermissions?.length > 0 && (
                <CollapsibleSection title="Knowledge Base">
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {agent.kbPermissions.map((p) => (
                      <DetailChip key={p} label={p} />
                    ))}
                  </Box>
                </CollapsibleSection>
              )}
            </Box>
            <Box
              sx={{
                px: 2,
                py: 2,
                flexShrink: 0,
                display: "flex",
                gap: "8px",
              }}
            >
              <Button
                onClick={onViewDetail}
                sx={{
                  width: canEdit ? 160 : "100%",
                  minWidth: canEdit ? 160 : "100%",
                  height: 40,
                  borderRadius: "8px",
                  background: "#148DFD",
                  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                  color: "#FFF",
                  fontFamily: "Inter",
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: "18px",
                  textTransform: "none",
                  flexShrink: 0,
                  "&:hover": { background: "#0077CC" },
                }}
              >
                View Detail
              </Button>
              {canEdit ? (
              <Button
                onClick={onEdit}
                sx={{
                  width: 80,
                  minWidth: 80,
                  height: 40,
                  borderRadius: "8px",
                  border: "1px solid #D5DCE1",
                  background: "#FFF",
                  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                  color: "#061522",
                  fontFamily: "Inter",
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: "18px",
                  textTransform: "none",
                  flexShrink: 0,
                  "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                }}
              >
                Edit
              </Button>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </>
    )
  );
}
